// src/pages/KOTManagementPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { listOfflineTables } from "../offline/tableStore";
import {
  deleteOfflineKotItem,
  getOfflineKotCounts,
  listOfflineKots,
  updateOfflineKotStatus,
} from "../offline/kotStore";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaChevronDown,
  FaChevronUp,
  FaSyncAlt,
  FaCheckCircle,
  FaBox,
  FaVolumeUp,
  FaUtensils,
} from "react-icons/fa";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "var(--app-bg, #f5f7fb)",
  card: "var(--app-surface, #fff)",
  line: "var(--app-border, #e5e7eb)",
  text: "var(--app-text, #0f172a)",
  muted: "var(--app-text-muted, #6b7280)",
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
};

const STATUS = {
  pending: "pending",
  prepared: "prepared",
  delivered: "delivered",
};

const LIST_KOT_API = API_ENDPOINTS.LIST_KOTS;
const ACTION_KOT_API = API_ENDPOINTS.UPDATE_KOT_STATUS;
const DELETE_KOT_API = API_ENDPOINTS.DELETE_KOT_ITEM;

const fmtINR = (n) => formatStoredMoney(n, { fractionDigits: 0 });

const parseNumeric = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const raw = String(value).trim();
  if (!raw) return NaN;
  const direct = Number(raw);
  if (Number.isFinite(direct)) return direct;
  const cleaned = raw.replace(/[^0-9.-]/g, "");
  if (!cleaned) return NaN;
  const fallback = Number(cleaned);
  return Number.isFinite(fallback) ? fallback : NaN;
};

const pickNumber = (...values) => {
  for (const value of values) {
    const n = parseNumeric(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const normalizeItems = (entry = {}) => {
  const direct = Array.isArray(entry?.items)
    ? entry.items
    : Array.isArray(entry?.itemlist)
    ? entry.itemlist
    : Array.isArray(entry?.kot_items)
    ? entry.kot_items
    : null;
  if (direct) return direct;

  const asString =
    typeof entry?.items === "string"
      ? entry.items
      : typeof entry?.itemlist === "string"
      ? entry.itemlist
      : typeof entry?.kot_items === "string"
      ? entry.kot_items
      : "";
  if (!asString) return [];
  try {
    const parsed = JSON.parse(asString);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const lineRate = (item = {}) =>
  pickNumber(
    item?.price,
    item?.rate,
    item?.priceaftertax,
    item?.price_after_tax,
    item?.taxedprice,
    item?.taxed_price,
    item?.unit_price
  );

const lineQty = (item = {}) => pickNumber(item?.qty, item?.quantity, 1);

const lineAmount = (item = {}) => {
  const direct = pickNumber(
    item?.amount,
    item?.total_amount,
    item?.line_total,
    item?.total
  );
  if (direct > 0) return direct;
  const qty = lineQty(item);
  const rate = lineRate(item);
  return Number((qty * rate).toFixed(2));
};

const toDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseMinutesValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value).trim();
  if (!raw) return 0;
  const num = Number(raw);
  if (!Number.isNaN(num)) return num;
  const match = raw.match(/(\\d+)/);
  return match ? Number(match[1]) || 0 : 0;
};

const diffMinutes = (start, end) => {
  if (!start || !end) return 0;
  const ms = end.getTime() - start.getTime();
  return ms <= 0 ? 0 : Math.round(ms / 60000);
};

const fmtDuration = (mins) => {
  const safe = Math.max(0, Number(mins) || 0);
  if (safe < 60) return `${safe}m`;
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const formatAmount = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "-";
  return fmtINR(n);
};

// ---- table helpers (mirrors mobile logic) ----
const normalizeTableKey = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const isTakeawayKey = (value) => {
  const raw = normalizeTableKey(value).toLowerCase();
  if (!raw) return false;
  return raw === "0" || raw === "-1" || raw.startsWith("takeaway");
};

const getTableKey = (table) =>
  normalizeTableKey(
    table?.table_key ??
      table?.tableKey ??
      table?.key ??
      table?.name ??
      table?.table_no ??
      table?.tableno ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      ""
  );

const getTableDisplayName = (table) =>
  normalizeTableKey(
    table?.display_name ??
      table?.displayName ??
      table?.tableDisplayName ??
      table?.table_name ??
      table?.name ??
      table?.table ??
      table?.table_no ??
      table?.tableno ??
      table?.table_key ??
      table?.tableKey ??
      ""
  );

const getTableType = (table) => {
  const raw = String(table?.table_type ?? table?.tableType ?? table?.type ?? "table").toLowerCase();
  if (isTakeawayKey(getTableKey(table))) return "takeaway";
  return raw === "room" ? "room" : "table";
};

const buildTableMetaMap = (list = []) => {
  const map = {};
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const key = getTableKey(entry);
    const numericName = entry?.name;
    const displayName = getTableDisplayName(entry);
    const tableName = normalizeTableKey(entry?.table_name ?? entry?.tableDisplayName ?? "");
    const type = getTableType(entry);
    const resolvedDisplay =
      displayName ||
      (key ? String(key) : numericName !== undefined ? String(numericName) : "");
    const meta = { displayName: resolvedDisplay, type };
    if (key) map[String(key)] = meta;
    if (numericName !== undefined && numericName !== null && numericName !== "") {
      map[String(numericName)] = meta;
    }
    if (tableName) map[String(tableName)] = meta;
    if (resolvedDisplay) map[String(resolvedDisplay)] = meta;
  });
  return map;
};
const resolveTableParts = (value, metaMap) => {
  const raw = normalizeTableKey(value);
  if (!raw) {
    return { prefix: "", displayName: "-", type: "table" };
  }
  if (isTakeawayKey(raw)) {
    const match = raw.toLowerCase().match(/takeaway[-\s]*(\d+)/);
    return {
      prefix: "Takeaway",
      displayName: match ? match[1] : "",
      type: "takeaway",
    };
  }
  const meta = metaMap[raw];
  const displayName = meta?.displayName || raw;
  const type = meta?.type === "room" ? "room" : "table";
  const prefix = type === "room" ? "Room" : "Table";
  return { prefix, displayName, type };
};
const buildPrefixedLabel = (prefix, displayName, separator = " ") => {
  if (!prefix) return displayName || "-";
  const display = String(displayName ?? "").trim();
  if (!display) return prefix;
  const lower = display.toLowerCase();
  const prefixLower = String(prefix).toLowerCase();
  if (lower.startsWith(prefixLower)) return display;
  return `${prefix}${separator}${display}`.trim();
};

const formatTableLabel = (order, tableMetaByKey = {}) => {
  const rawKey =
    order?.table ??
    order?.tableno ??
    order?.table_no ??
    order?.tableid ??
    order?.table_id ??
    order?.tableDisplayName ??
    order?.display_name ??
    order?.table_name ??
    "";
  const base = resolveTableParts(rawKey, tableMetaByKey);
  const fallbackName =
    order?.tableDisplayName ??
    order?.display_name ??
    order?.table_name ??
    null;
  const fallbackTypeRaw =
    order?.tableType ?? order?.table_type ?? order?.type ?? null;
  const fallbackType = String(fallbackTypeRaw || "").trim().toLowerCase();
  const fallbackNameLower = String(fallbackName || "").trim().toLowerCase();
  const displayName = fallbackName || base.displayName || "-";
  const baseType =
    base.type === "takeaway" ? "takeaway" : base.type || "table";
  const type =
    baseType === "takeaway"
      ? "takeaway"
      : fallbackType === "room" || fallbackNameLower.startsWith("room")
      ? "room"
      : baseType;
  const prefix =
    type === "takeaway" ? "Takeaway" : type === "room" ? "Room" : "Table";
  return buildPrefixedLabel(prefix, displayName, " ");
};

const KOTManagementPage = () => {
  const fullOfflineMode = isFullOfflineModeEnabled();
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState({});
  const [appValid, setAppValid] = useState(true);
  const [validityLoading, setValidityLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [tab, setTab] = useState(STATUS.pending);
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, prepared: 0, delivered: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [timingMap, setTimingMap] = useState({});
  const [tableMetaByKey, setTableMetaByKey] = useState({});

  const timingKey = useMemo(
    () => (userId
      ? `${fullOfflineMode ? "offlineKotTimingMap" : "kotTimingMap"}:${userId}`
      : null),
    [fullOfflineMode, userId]
  );

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    const restaurantId =
      data.restaurantid || data.restaurant_id || data.owner_id ||
      data.ownerid || data.parent_id || data.id;
    setUser(data);
    setUserId(restaurantId);
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (fullOfflineMode) {
      setAppValid(true);
      setValidityLoading(false);
      return undefined;
    }
    let active = true;
    const checkValidity = async () => {
      setValidityLoading(true);
      try {
        const response = await fetch(API_ENDPOINTS.CHECK_VALID, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        });
        const result = await response.json().catch(() => ({}));
        if (active) setAppValid(response.ok && result?.success !== false);
      } catch {
        // A temporary network failure must not incorrectly mark a paid account expired.
        if (active) setAppValid(true);
      } finally {
        if (active) setValidityLoading(false);
      }
    };
    checkValidity();
    const timer = window.setInterval(checkValidity, 5 * 60 * 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, [userId, fullOfflineMode]);

  useEffect(() => {
    let active = true;
    const loadTableMeta = async () => {
      try {
        if (fullOfflineMode && userId) {
          const tables = await listOfflineTables(userId);
          if (active) setTableMetaByKey(buildTableMetaMap(tables));
          return;
        }
        const raw = localStorage.getItem("tablelistx");
        if (!raw) return;
        const tables = JSON.parse(raw);
        if (active && Array.isArray(tables)) setTableMetaByKey(buildTableMetaMap(tables));
      } catch {
        if (active) setTableMetaByKey({});
      }
    };
    loadTableMeta();
    return () => { active = false; };
  }, [fullOfflineMode, userId]);

  useEffect(() => {
    if (!timingKey) return;
    const raw = localStorage.getItem(timingKey);
    if (!raw) {
      setTimingMap({});
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setTimingMap(parsed && typeof parsed === "object" ? parsed : {});
    } catch {
      setTimingMap({});
    }
  }, [timingKey]);

  useEffect(() => {
    if (!userId || validityLoading || !appValid) return;
    fetchCounts();
    fetchKOT();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tab, fromDate, toDate, validityLoading, appValid]);

  useEffect(() => {
    if (!userId || !autoRefresh || !appValid) return undefined;
    const timer = window.setInterval(() => {
      fetchCounts();
      fetchKOT({ silent: true });
    }, 10000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, autoRefresh, appValid, tab, fromDate, toDate]);

  const fetchCounts = async () => {
    try {
      if (fullOfflineMode) {
        const result = await getOfflineKotCounts(userId, fromDate, toDate);
        setCounts(result?.counts || { pending: 0, prepared: 0, delivered: 0 });
        return;
      }
      const statuses = [STATUS.pending, STATUS.prepared, STATUS.delivered];
      const resArr = await Promise.all(
        statuses.map((status) =>
          fetch(LIST_KOT_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              restaurantid: userId,
              status,
              fromdate: fromDate,
              todate: toDate,
            }),
          }).then((r) => r.json().catch(() => ({})))
        )
      );
      setCounts({
        pending: resArr[0]?.kot_items?.length || 0,
        prepared: resArr[1]?.kot_items?.length || 0,
        delivered: resArr[2]?.kot_items?.length || 0,
      });
    } catch (e) {
      // ignore counts errors
    }
  };

  const fetchKOT = async (opts = {}) => {
    if (!userId) return;
    if (opts.refresh) setRefreshing(true);
    else if (!opts.silent) setLoading(true);
    try {
      let json;
      if (fullOfflineMode) {
        json = await listOfflineKots(userId, tab, fromDate, toDate);
      } else {
        const res = await fetch(LIST_KOT_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: userId,
            status: tab,
            fromdate: fromDate,
            todate: toDate,
          }),
        });
        json = await res.json();
        if (!res.ok || json?.success === false)
          throw new Error(json?.message || "Failed to load KOTs");
      }
      const rows = Array.isArray(json.kot_items) ? json.kot_items : [];

      if (timingKey) {
        const next = { ...(timingMap || {}) };
        let changed = false;
        const nowIso = new Date().toISOString();
        rows.forEach((item) => {
          const id = Number(item.kotitem_id || item.id);
          if (!id) return;
          const prev = next[id] || {};
          const createdMinutes = parseMinutesValue(
            item.created_minutes_ago ?? item.minutes_ago ?? item.created_min_ago
          );
          const fallbackCreated =
            createdMinutes > 0 ? new Date(Date.now() - createdMinutes * 60000).toISOString() : null;
          const createdAtRaw =
            prev.createdAt ||
            item.created_at ||
            item.createdAt ||
            item.start_time ||
            fallbackCreated ||
            nowIso;
          const statusRaw = String(item.status || tab || "").trim().toLowerCase();
          const preparedAtRaw =
            prev.preparedAt ||
            item.prepared_at ||
            (statusRaw === STATUS.prepared || statusRaw === STATUS.delivered
              ? item.updated_at
              : null);
          const deliveredAtRaw =
            prev.deliveredAt ||
            item.delivered_at ||
            (statusRaw === STATUS.delivered ? item.updated_at : null);

          let preparedAt = preparedAtRaw ? new Date(preparedAtRaw) : null;
          let deliveredAt = deliveredAtRaw ? new Date(deliveredAtRaw) : null;

          if ((statusRaw === STATUS.prepared || statusRaw === STATUS.delivered) && !preparedAt) {
            preparedAt = new Date(nowIso);
          }
          if (statusRaw === STATUS.delivered && !deliveredAt) {
            deliveredAt = new Date(nowIso);
          }
          if (deliveredAt && !preparedAt) {
            preparedAt = deliveredAt;
          }

          const nextEntry = {
            createdAt: new Date(createdAtRaw).toISOString(),
            preparedAt: preparedAt ? preparedAt.toISOString() : null,
            deliveredAt: deliveredAt ? deliveredAt.toISOString() : null,
          };

          if (
            prev.createdAt !== nextEntry.createdAt ||
            prev.preparedAt !== nextEntry.preparedAt ||
            prev.deliveredAt !== nextEntry.deliveredAt
          ) {
            next[id] = nextEntry;
            changed = true;
          }
        });
        if (changed) {
          setTimingMap(next);
          localStorage.setItem(timingKey, JSON.stringify(next));
        }
      }

      setList(rows);
      setLastUpdated(new Date());
    } catch (e) {
      toast.error(e.message || "Unable to load KOTs");
      setList([]);
    } finally {
      if (opts.refresh) setRefreshing(false);
      else if (!opts.silent) setLoading(false);
    }
  };

  const updateStatus = async (itemId, next) => {
    try {
      let json;
      if (fullOfflineMode) {
        json = await updateOfflineKotStatus(userId, itemId, next);
      } else {
        const res = await fetch(ACTION_KOT_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            kotitem_id: itemId,
            status: next,
            restaurantid: userId,
          }),
        });
        json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.kotitem)
          throw new Error(json?.message || "Failed to update");
      }
      toast.success(json?.message || "Updated");
      setList((prev) => prev.filter((i) => (i.kotitem_id || i.id) !== itemId));
      if (timingKey) {
        const nowIso = new Date().toISOString();
        setTimingMap((prev) => {
          const nextMap = { ...(prev || {}) };
          const existing = nextMap[itemId] || {};
          const createdAt = existing.createdAt || nowIso;
          if (next === STATUS.prepared) {
            nextMap[itemId] = {
              createdAt,
              preparedAt: nowIso,
              deliveredAt: existing.deliveredAt || null,
            };
          } else if (next === STATUS.delivered) {
            nextMap[itemId] = {
              createdAt,
              preparedAt: existing.preparedAt || nowIso,
              deliveredAt: nowIso,
            };
          }
          localStorage.setItem(timingKey, JSON.stringify(nextMap));
          return nextMap;
        });
      }
      fetchCounts();
    } catch (e) {
      toast.error(e.message || "Unable to update");
    }
  };

  const deleteKotItem = async (itemId) => {
    if (!window.confirm("Delete this KOT item?")) return;
    try {
      let json;
      if (fullOfflineMode) {
        json = await deleteOfflineKotItem(userId, itemId);
      } else {
        const res = await fetch(DELETE_KOT_API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kotitem_id: itemId, restaurantid: userId }) });
        json = await res.json().catch(() => ({}));
        if (!res.ok || json?.success === false) throw new Error(json?.message || "Unable to delete KOT item");
      }
      setList((prev) => prev.filter((item) => (item.kotitem_id || item.id) !== itemId));
      toast.success(json?.message || "KOT item deleted");
      fetchCounts();
    } catch (e) { toast.error(e.message); }
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: prev[id] === false }));

  const tabMeta = {
    [STATUS.pending]: { label: "Pending", color: COLOR.amber },
    [STATUS.prepared]: { label: "Prepared", color: COLOR.green },
    [STATUS.delivered]: { label: "Delivered", color: COLOR.primary },
  };

  const totalItems = useMemo(
    () =>
      list.reduce(
        (sum, o) => sum + (Number(o.total_items) || (Array.isArray(o.items) ? o.items.length : 1)),
        0
      ),
    [list]
  );

  const isChef = String(user?.role || user?.staffrole || user?.staff_role || "").toLowerCase() === "chef";

  if (!validityLoading && !appValid) {
    return <ExpiredPage><div><span>SUBSCRIPTION REQUIRED</span><FaUtensils /><h1>Kitchen display is locked</h1><p>Your restaurant owner’s plan has expired. Ask the owner to renew the plan, then refresh this screen.</p><button onClick={() => window.location.reload()}>Check again</button><button className="logout" onClick={() => { localStorage.removeItem("userData"); window.location.href = "/login"; }}>Logout</button></div></ExpiredPage>;
  }

  if (loading && !list.length) {
    return (
      <Center>
        <div className="spinner" />
        <div>Loading KOTs…</div>
      </Center>
    );
  }

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <TopBar>
        <HeroCopy>
          <KitchenMark><FaUtensils /></KitchenMark>
          <div>
          <Eyebrow>LIVE KITCHEN</Eyebrow>
          <Title>Kitchen Display System</Title>
          <Sub>{isChef ? `Chef ${user?.name || ""} · ` : ""}Prepare, track and dispatch every ticket from one screen.</Sub>
          <PillRow>
            <Pill>Total: {list.length}</Pill>
            <Pill $muted>Items: {totalItems}</Pill>
          </PillRow>
          </div>
        </HeroCopy>
        <Actions>
          <LiveToggle type="button" $active={autoRefresh} onClick={() => setAutoRefresh((value) => !value)}><FaVolumeUp /> {autoRefresh ? "Live · 10s" : "Live off"}</LiveToggle>
          <DateInput>
            From:
            <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} />
          </DateInput>
          <DateInput>
            To:
            <input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} />
          </DateInput>
          <PrimaryBtn onClick={() => fetchKOT({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
          {lastUpdated ? <Updated>Updated {lastUpdated.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</Updated> : null}
        </Actions>
      </TopBar>

      <Tabs>
        {Object.keys(tabMeta).map((key) => {
          const active = tab === key;
          const meta = tabMeta[key];
          const count =
            key === STATUS.pending
              ? counts.pending
              : key === STATUS.prepared
              ? counts.prepared
              : counts.delivered;
          return (
            <TabBtn key={key} $active={active} $color={meta.color} onClick={() => setTab(key)}>
              <TabIcon $color={meta.color}>{key === STATUS.pending ? "🔥" : key === STATUS.prepared ? "✓" : "↗"}</TabIcon>
              <TabCopy><small>{meta.label}</small><strong>{count}</strong></TabCopy>
              <span className="hint">{key === STATUS.pending ? "Needs attention" : key === STATUS.prepared ? "Ready to serve" : "Completed"}</span>
            </TabBtn>
          );
        })}
      </Tabs>

      {loading ? (
        <Center>Loading…</Center>
      ) : list.length === 0 ? (
        <Center>No KOTs for this range/status.</Center>
      ) : (
        <List>
          {list.map((o) => {
              const isExpanded = expanded[o.kotitem_id || o.id] !== false;
            const status = tabMeta[tab];
            const isPending = tab === STATUS.pending;
            const isPrepared = tab === STATUS.prepared;
            const items = normalizeItems(o);
            const displayItems =
              items.length > 0
                ? items
              : o.product_name
              ? [
                  {
                    product_name: o.product_name,
                    qty: o.qty,
                    rate: lineRate(o),
                  },
                ]
              : [];
            const totalQty =
              displayItems.length > 0
                ? displayItems.reduce((s, it) => s + lineQty(it), 0)
                : Number(o.qty || 0);
            const derivedAmount =
              displayItems.length > 0
                ? displayItems.reduce((sum, it) => sum + lineAmount(it), 0)
                : lineAmount(o);
            const cardAmount = pickNumber(
              o.total_amount,
              o.amount,
              o.bill_amount,
              o.grand_total,
              o.totalamount,
              o.totalAmount,
              derivedAmount
            );
            const createdMinutes = parseMinutesValue(
              o.created_minutes_ago ?? o.minutes_ago ?? o.created_min_ago
            );
            const createdAtFallback =
              createdMinutes > 0 ? new Date(Date.now() - createdMinutes * 60000) : null;
            const timingEntry = timingMap?.[Number(o.kotitem_id || o.id)];
            const createdAt =
              toDateSafe(timingEntry?.createdAt) ||
              toDateSafe(o.created_at) ||
              toDateSafe(o.createdAt) ||
              toDateSafe(o.start_time) ||
              createdAtFallback ||
              new Date();
            const preparedAt =
              toDateSafe(timingEntry?.preparedAt) ||
              toDateSafe(o.prepared_at) ||
              (tab !== STATUS.pending ? toDateSafe(o.updated_at) : null);
            const deliveredAt =
              toDateSafe(timingEntry?.deliveredAt) ||
              toDateSafe(o.delivered_at) ||
              (tab === STATUS.delivered ? toDateSafe(o.updated_at) : null);
            const now = new Date();
            const pendingMin = preparedAt
              ? diffMinutes(createdAt, preparedAt)
              : createdMinutes > 0
              ? createdMinutes
              : diffMinutes(createdAt, now);
            const deliveryMin = preparedAt && deliveredAt ? diffMinutes(preparedAt, deliveredAt) : 0;
            const totalMin = deliveredAt ? diffMinutes(createdAt, deliveredAt) : pendingMin;
            const waitingMin = preparedAt && !deliveredAt ? diffMinutes(preparedAt, now) : 0;

            const statusTimeText = isPending
              ? `Pending time: ${fmtDuration(pendingMin)}`
              : isPrepared
              ? `Prep time: ${fmtDuration(pendingMin)}`
              : `Total time: ${fmtDuration(totalMin)}`;

            const timingDetail = isPending
              ? ""
              : isPrepared
              ? `Pending time: ${fmtDuration(pendingMin)} • Waiting delivery: ${fmtDuration(waitingMin)}`
              : `Pending time: ${fmtDuration(pendingMin)} • Delivery time: ${fmtDuration(deliveryMin)} • Total time: ${fmtDuration(totalMin)}`;
            return (
              <Card key={o.kotitem_id || o.id} $status={tab} $urgent={isPending && pendingMin >= 20}>
                <CardHead>
                  <div>
                    <CardTitle>{formatTableLabel(o, tableMetaByKey)}</CardTitle>
                    <CardMeta>
                      Order #{o.order_id || o.orderid || o.kotid || o.id} ·{" "}
                      {createdAt.toLocaleString("en-IN")}
                    </CardMeta>
                  </div>
                  <StatusChip style={{ background: status.color + "22", color: status.color }}>
                    {status.label}
                  </StatusChip>
                </CardHead>
                <CardMetaRow>
                  <MetaPill>Total Items: {displayItems.length}</MetaPill>
                  <MetaPill>Total Qty: {totalQty}</MetaPill>
                  <MetaPill>Amount: {formatAmount(cardAmount)}</MetaPill>
                  <MetaPill>{statusTimeText}</MetaPill>
                </CardMetaRow>
                {timingDetail ? <TimingNote>{timingDetail}</TimingNote> : null}
                <Divider />
                <ExpandRow onClick={() => toggleExpand(o.kotitem_id || o.id)}>
                  <span>{isExpanded ? "Hide items" : "View items"}</span>
                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </ExpandRow>
                {isExpanded && displayItems.length > 0 && (
                  <Items>
                    {displayItems.map((it, idx) => (
                      <ItemRow key={idx}>
                        <ItemName>{it.name || it.product_name}</ItemName>
                        <ItemQty>× {lineQty(it)}</ItemQty>
                        <ItemAmt>{formatAmount(lineAmount(it))}</ItemAmt>
                      </ItemRow>
                    ))}
                  </Items>
                )}
                <ActionRow>
                  {!isChef && <DangerBtn onClick={() => deleteKotItem(o.kotitem_id || o.id)}>Delete</DangerBtn>}
                  {tab === STATUS.pending && (
                    <>
                      <PrimaryBtn small onClick={() => updateStatus(o.kotitem_id || o.id, STATUS.prepared)}>
                        <FaCheckCircle /> Prepared
                      </PrimaryBtn>
                      <GhostBtn onClick={() => updateStatus(o.kotitem_id || o.id, STATUS.delivered)}>
                        <FaBox /> Delivered
                      </GhostBtn>
                    </>
                  )}
                  {tab === STATUS.prepared && (
                    <PrimaryBtn small onClick={() => updateStatus(o.kotitem_id || o.id, STATUS.delivered)}>
                      <FaBox /> Mark Delivered
                    </PrimaryBtn>
                  )}
                </ActionRow>
              </Card>
            );
          })}
        </List>
      )}
    </Page>
  );
};

export default KOTManagementPage;

const today = () => {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------- styled ---------------- */
const Page = styled.div`
  background:
    linear-gradient(color-mix(in srgb, var(--app-border) 24%, transparent) 1px, transparent 1px),
    linear-gradient(90deg, color-mix(in srgb, var(--app-border) 24%, transparent) 1px, transparent 1px),
    var(--app-bg);
  background-size: 32px 32px;
  min-height: 100vh;
  padding: clamp(14px, 1.7vw, 22px);
`;
const Eyebrow = styled.div`
  color: var(--app-primary-hover); font-size: 11px; font-weight: 900; letter-spacing: .2em; margin-bottom: 5px;
`;
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 18px 20px;
  border-radius: 18px;
  color: var(--app-text);
  background:
    radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--app-primary) 18%, transparent), transparent 28%),
    var(--app-surface);
  border:1px solid var(--app-border);
  box-shadow: 0 12px 32px rgba(0,0,0,.24);
  position: relative;
  overflow: hidden;
`;
const HeroCopy = styled.div`display:flex;align-items:center;gap:16px;min-width:min(100%,420px);`;
const KitchenMark = styled.div`
  width:50px;height:50px;flex:0 0 50px;border-radius:14px;display:grid;place-items:center;
  font-size:21px;background:var(--app-primary);color:#fff;border:1px solid var(--app-primary-hover);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.2);
`;
const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.3rem, 2vw, 1.8rem);
  font-weight: 900;
  color: var(--app-text);
  letter-spacing: -.035em;
`;
const Sub = styled.div`
  color: var(--app-text-muted);
  margin-top: 4px;
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: ${({ $muted }) => ($muted ? "var(--app-surface-raised)" : "rgba(22,163,74,.14)")};
  color: ${({ $muted }) => ($muted ? COLOR.text : "#16a34a")};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid var(--app-border);
`;
const Updated = styled.small`color:var(--app-text-muted); width:100%; text-align:right;`;
const LiveToggle = styled.button`
  border: 1px solid ${({ $active }) => ($active ? "#22c55e" : COLOR.line)};
  background: ${({ $active }) => ($active ? "rgba(22,163,74,.14)" : COLOR.card)};
  color: ${({ $active }) => ($active ? "#16a34a" : COLOR.text)};
  padding: 10px 12px; border-radius: 999px; font-weight: 850; cursor: pointer;
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-left: auto;
  max-width: 100%;

  @media (max-width: 820px) {
    width: 100%;
    margin-left: 0;
  }
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-input-bg);
  color: var(--app-text);
  input {
    border: 0;
    background: transparent;
    color: var(--app-text);
    outline: none;
  }
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ small }) => (small ? "8px 10px" : "10px 12px")};
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const GhostBtn = styled(PrimaryBtn)`
  background: var(--app-primary-soft);
  color: ${COLOR.primary};
  border: 1px solid ${COLOR.primary};
`;
const DangerBtn = styled(PrimaryBtn)`background:rgba(190,18,60,.16);color:#fb7185;border:1px solid rgba(251,113,133,.45);`;
const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;
const TabBtn = styled.button`
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 13px;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : COLOR.line)};
  background: ${({ $active }) => ($active ? COLOR.card : "var(--app-surface-raised, #f3f6fb)")};
  color: ${COLOR.text};
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${({ $active, $color }) => $active ? `inset 0 -3px 0 ${$color}, 0 8px 20px rgba(0,0,0,.2)` : "0 7px 18px rgba(0,0,0,.16)"};
  transition: transform .18s ease, border-color .18s ease;
  &:hover{transform:translateY(-2px)}
  .hint{justify-self:end;color:${COLOR.muted};font-size:12px;font-weight:700}
  @media(max-width:680px){.hint{display:none}}
`;
const TabIcon = styled.span`width:38px;height:38px;border-radius:12px;display:grid;place-items:center;background:${({$color})=>$color}18;color:${({$color})=>$color};font-size:18px;`;
const TabCopy = styled.span`display:grid;text-align:left;small{color:${COLOR.muted};font-size:11px;text-transform:uppercase;letter-spacing:.08em}strong{font-size:20px;color:${COLOR.text}}`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(300px, 1fr));
  grid-auto-rows: auto;
  gap: 14px;
  align-items: stretch;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${({$urgent})=>$urgent ? "#f97316" : COLOR.line};
  border-left: 5px solid ${({$status})=>$status === STATUS.pending ? "#f59e0b" : $status === STATUS.prepared ? "#22c55e" : "#38bdf8"};
  border-radius: 15px;
  padding: 17px;
  box-shadow: ${({$urgent})=>$urgent ? "0 14px 38px rgba(249,115,22,.2)" : "var(--app-shadow)"};
  height: 100%;
  display: flex;
  flex-direction: column;
  position:relative;
  transition:transform .18s ease, box-shadow .18s ease;
  &:hover{transform:translateY(-2px);box-shadow:0 18px 42px rgba(0,0,0,.3)}
  ${({$urgent})=>$urgent ? `&:after{content:"LATE";position:absolute;right:14px;top:-11px;background:#ea580c;color:white;padding:3px 8px;border-radius:999px;font-size:9px;font-weight:900;letter-spacing:.1em}` : ""}
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;
const CardTitle = styled.div`
  font-weight: 900;
  color: ${COLOR.text};
  font-size: 1.15rem;
`;
const CardMeta = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
`;
const StatusChip = styled.span`
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
  text-transform: uppercase;
  letter-spacing: .06em;
  font-size: 11px;
`;
const CardMetaRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
  color: ${COLOR.muted};
  font-size: 13px;
`;
const TimingNote = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${COLOR.muted};
`;
const MetaPill = styled.span`
  background: var(--app-surface-raised, #f3f6fb);
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid ${COLOR.line};
`;
const Divider = styled.div`
  height: 1px;
  background: ${COLOR.line};
  margin: 10px 0;
`;
const ExpandRow = styled.button`
  border: none;
  background: var(--app-surface-raised, #f9fafb);
  border-radius: 10px;
  padding: 10px 12px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: ${COLOR.text};
  font-weight: 700;
`;
const Items = styled.div`
  margin-top: 8px;
  display: grid;
  gap: 6px;
`;
const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: var(--app-input-bg);
`;
const ExpiredPage = styled.div`
  min-height: calc(100vh - 80px); display:grid; place-items:center; padding:24px;
  background:${COLOR.bg}; color:${COLOR.text};
  > div { width:min(520px,100%); text-align:center; padding:42px; border-radius:24px; background:${COLOR.card}; border:1px solid ${COLOR.line}; box-shadow:0 24px 70px rgba(0,0,0,.2); }
  svg { display:block; margin:18px auto; font-size:52px; color:#f59e0b; }
  span { color:#f59e0b; font-weight:900; letter-spacing:.14em; font-size:12px; }
  h1 { margin:0 0 10px; } p { color:${COLOR.muted}; line-height:1.6; }
  button { border:0; border-radius:12px; padding:12px 18px; margin:8px 4px 0; background:var(--app-primary); color:white; font-weight:850; cursor:pointer; }
  button.logout { background:transparent; color:${COLOR.text}; border:1px solid ${COLOR.line}; }
`;
const ItemName = styled.div`
  font-weight: 700;
  color: ${COLOR.text};
  min-width: 0;
`;
const ItemQty = styled.div`
  color: var(--app-primary-hover);
  font-weight: 900;
  font-size: 1.08rem;
`;
const ItemAmt = styled.div`
  text-align: right;
  font-weight: 800;
  color: ${COLOR.text};
`;
const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding-top: 10px;
  flex-wrap: wrap;
  > button { min-height:44px; justify-content:center; flex:1; }
`;

const Center = styled.div`
  display: grid;
  place-items: center;
  min-height: 30vh;
  color: ${COLOR.muted};
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: ${COLOR.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
